import React from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Turnstile } from '@marsidev/react-turnstile';
import { User, Mail, Phone, MessageSquare } from 'lucide-react';
import { ReservationData } from '../types/reservation.types';

interface StepContactProps {
  data: ReservationData;
  onUpdate: (updates: Partial<ReservationData>) => void;
}

const TURNSTILE_SITE_KEY = '1x00000000000000000000AA'; // Test key - replace with real key

const StepContact: React.FC<StepContactProps> = ({ data, onUpdate }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-4 py-2 px-1"
    >
      <div className="text-center mb-2">
        <h3 className="text-sm font-semibold text-foreground">Vos coordonnées</h3>
        <p className="text-xs text-muted-foreground">Pour confirmer votre réservation</p>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor="guest-name" className="flex items-center gap-1.5 text-xs mb-1">
            <User className="h-3.5 w-3.5" /> Prénom et nom *
          </Label>
          <Input
            id="guest-name"
            value={data.guestName}
            onChange={e => onUpdate({ guestName: e.target.value })}
            placeholder="Jean Dupont"
            className="h-11"
            maxLength={100}
          />
        </div>

        <div>
          <Label htmlFor="guest-email" className="flex items-center gap-1.5 text-xs mb-1">
            <Mail className="h-3.5 w-3.5" /> Email *
          </Label>
          <Input
            id="guest-email"
            type="email"
            value={data.guestEmail}
            onChange={e => onUpdate({ guestEmail: e.target.value })}
            placeholder="jean@exemple.fr"
            className="h-11"
            maxLength={255}
          />
        </div>

        <div>
          <Label htmlFor="guest-phone" className="flex items-center gap-1.5 text-xs mb-1">
            <Phone className="h-3.5 w-3.5" /> Téléphone *
          </Label>
          <Input
            id="guest-phone"
            type="tel"
            value={data.guestPhone}
            onChange={e => onUpdate({ guestPhone: e.target.value })}
            placeholder="+33 6 12 34 56 78"
            className="h-11"
            maxLength={20}
          />
        </div>

        <div>
          <Label htmlFor="guest-notes" className="flex items-center gap-1.5 text-xs mb-1">
            <MessageSquare className="h-3.5 w-3.5" /> Notes (optionnel)
          </Label>
          <Textarea
            id="guest-notes"
            value={data.notes}
            onChange={e => onUpdate({ notes: e.target.value })}
            placeholder="Allergies, occasion spéciale, chaise haute…"
            rows={2}
            maxLength={500}
            className="resize-none"
          />
        </div>

        <div className="flex items-start gap-2 pt-1">
          <Checkbox
            id="gdpr"
            checked={data.gdprConsent}
            onCheckedChange={(v) => onUpdate({ gdprConsent: v === true })}
            className="mt-0.5"
          />
          <Label htmlFor="gdpr" className="text-xs text-muted-foreground leading-tight cursor-pointer">
            J'accepte que mes données soient utilisées pour cette réservation conformément au RGPD
          </Label>
        </div>

        <div className="flex justify-center pt-2">
          <Turnstile
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={(token) => onUpdate({ turnstileToken: token })}
            onError={() => onUpdate({ turnstileToken: null })}
            onExpire={() => onUpdate({ turnstileToken: null })}
            options={{ theme: 'light', size: 'compact' }}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default StepContact;
